import { type DefaultObject } from '@moniebook-web/types';
import { type ErrorResponse, getApiErrorMessage } from '@moniebook-web/utils';
import type { AxiosError } from 'axios';
import { Axios } from '../api';
import { invalidateQuery } from '../cache';
import { REGISTERS_API_URL } from '../constants';
import { type AppDatabase } from '../db';
import { type RemoteCart } from '../models';
import { QUERY_KEYS } from '../query-keys';
import { type MappedRemoteCartResponse } from '../types';

export class RemoteCartService {
    constructor(private readonly db: AppDatabase) {
        this.db = db;
    }

    async getCarts() {
        const carts = await this.db.carts
            .orderBy('createdAt')
            .reverse()
            .toArray();

        return carts.filter((cart) => cart.cartVersion === 1);
    }

    async insertCart(params: RemoteCart) {
        await this.db.carts.add(params);
    }

    async getCart(id: string) {
        return this.db.carts.get(id);
    }

    async deleteCart(id: string) {
        await this.db.carts.delete(id);
    }

    async editSavedCartDetails(params: RemoteCart) {
        await this.db.carts.update(params.id, { ...params });
    }

    async syncCarts(cart: RemoteCart) {
        const savedCart = await this.db.carts.get(cart?.id);

        if (!savedCart?.id) {
            return { success: false, error: 'Saved cart not found' };
        }

        try {
            await Axios.put<MappedRemoteCartResponse>(
                REGISTERS_API_URL.REGISTER.CREATE_SAVED_CART,
                savedCart,
            );

            await this.db.carts.delete(savedCart.id);

            await invalidateQuery([QUERY_KEYS.GET_PAGINATED_SAVED_CARTS]);

            return { success: true, error: null };
        } catch (error) {
            const axiosError = error as AxiosError;

            const errorMessage = getApiErrorMessage(
                axiosError as unknown as Partial<ErrorResponse<DefaultObject>>,
            );

            return {
                success: false,
                error: errorMessage ?? 'Unknown error',
            };
        }
    }
}
